import {
  collection,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import readXlsxFile from 'read-excel-file';
import { v4 } from 'uuid';
import { useAuth } from '~/hooks/auth';
import { useToast } from '~/hooks/toast';
import ICategory from '~/interfaces/ICategory';
import IProduct from '~/interfaces/IProduct';
import { DB } from '~/services/firebase';
import { getImportDate } from '~/utils/getImportDate';
import { getPhoneFormatted } from '~/utils/getPhoneFormatted';

// interface ProductImport {
//   barcode: string;
//   name: string;
//   value: string;
//   category: string;
//   status: string;
//   description: string;
// }

interface SettingsImportProps {
  openMenu(): void;
}

const SettingsImport: React.FC<SettingsImportProps> = ({ openMenu }) => {
  const { user, company: companyData } = useAuth();
  const { addToast } = useToast();
  const [company] = useState(companyData);
  const [loading, setLoading] = useState(false);

  // useEffect(() => {
  //   async function loadCompany() {
  //     const companiesRef = collection(DB, 'companies');
  //     const q = query(companiesRef, where('user_id', '==', user.uid));
  //     const querySnapshot = await getDocs(q);

  //     if (querySnapshot.docs.length > 0) {
  //       const aux = querySnapshot.docs[0].data() as ICompany;
  //       const item = {
  //         ...aux,
  //         uid: querySnapshot.docs[0].id,
  //       };

  //       setCompany(item);

  //       const inputColor = document.getElementById('color') as any;
  //       if (inputColor) {
  //         inputColor.value = item.app_color;
  //       }
  //     }
  //   }

  //   loadCompany();
  // }, [user.uid]);

  const getCategories = useCallback(async () => {
    const q = query(
      collection(DB, 'categories'),
      where('user_id', '==', user.uid),
    );
    const querySnapshot = await getDocs(q);

    const items: ICategory[] = [];

    querySnapshot.docs.forEach(item => {
      const parseItem = {
        ...item.data(),
        uid: item.id,
      } as ICategory;

      items.push(parseItem);
    });

    return items;
  }, [user]);

  const bulkInsertPlaces = useCallback(
    async (rows: any[]) => {
      const batch = writeBatch(DB);

      rows.forEach((row, index) => {
        if (index > 0) {
          const [district, value] = row;

          const distance = {
            uid: v4(),
            district,
            value: parseFloat(value),
            company_id: company.uid,
            user_id: user.uid,
            type: '1',
            from: 0,
            to: 0,
            status: true,
          };

          batch.set(doc(DB, 'distances', distance.uid), distance);
        }
      });

      await batch.commit();
    },
    [company.uid, user.uid],
  );

  async function chunckBulkInsertClients(rows: any[], index: number) {
    const chunkSize = 300;
    const i = index * chunkSize;
    const chunk = rows.slice(i === 0 ? 1 : i, i + chunkSize);

    if (chunk.length > 0) {
      await bulkInsertClients(chunk);
      console.log(`persistiu total ${chunk.length}`);
      await chunckBulkInsertClients(rows, index + 1);
    }
  }

  const bulkInsertClients = useCallback(
    async (rows: any[]) => {
      try {
        const batch = writeBatch(DB);

        rows.forEach(async (row, index) => {
          const [
            name,
            phone,
            email,
            genre,
            birthdate,
            created_at,
            last_order,
            count_orders,
            average_ticket,
            fidelit_points,
            street,
            number,
            neighborhood,
            complement,
            lot,
            city,
            state,
            reference,
            cep,
          ] = row;

          let address = street;
          if (number) address += `, Nº ${number}`;
          if (neighborhood) address += `, ${neighborhood}`;
          if (complement) address += `, ${complement}`;
          if (lot) address += `, ${lot}`;
          if (city) address += `, ${city}`;
          if (state) address += ` - ${state}`;
          if (cep) address += `, ${cep}`;
          if (reference) address += `, ${reference}`;

          const client = {
            uid: v4(),
            name: String(name),
            whatsapp: getPhoneFormatted(phone),
            email,
            genre,
            birthdate,
            created_at: getImportDate(created_at),
            last_order: getImportDate(last_order),
            count_orders,
            average_ticket,
            fidelit_points,
            street,
            number,
            neighborhood,
            complement,
            lot,
            city,
            state,
            reference,
            cep,
            address,
            company_id: company.uid,
            user_id: user.uid,
            status: true,
          };

          batch.set(doc(DB, 'clients', client.uid), client);
        });

        await batch.commit();
      } catch (err) {
        console.log(err);
      }
    },
    [company.uid, user.uid],
  );

  const bulkInsert = useCallback(
    async (rows: any[]) => {
      const categories = await getCategories();
      const newCategories: ICategory[] = [];

      const batch = writeBatch(DB);

      rows.forEach(async (row, index) => {
        if (index > 0 && row) {
          const [
            barcode,
            category_name,
            measure,
            name,
            value,
            status,
            description,
            image,
          ] = row;

          const product = {
            uid: v4(),
            barcode,
            name,
            measure,
            value,
            status: status === 'T',
            description,
            image,
            company_id: company.uid,
            user_id: user.uid,
          } as IProduct;

          const findCategory = categories.find(
            item => item.name === category_name,
          );

          const findNewCategory = newCategories.find(
            item => item.name === category_name,
          );

          if (findCategory) {
            product.category = findCategory;
          } else if (findNewCategory) {
            product.category = findNewCategory;
          } else {
            const new_category = {
              uid: v4(),
              name: category_name,
              company_id: company.uid,
              user_id: user.uid,
              status: true,
            } as ICategory;

            newCategories.push(new_category);
            batch.set(doc(DB, 'categories', new_category.uid), new_category);
            product.category = new_category;
          }

          batch.set(doc(DB, 'products', product.uid), product);
        }
      });

      await batch.commit();
    },
    [getCategories, company, user],
  );

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        let inputFile = document.getElementById('import') as HTMLInputElement;
        if (inputFile && inputFile.files && inputFile.files.length > 0) {
          const file = inputFile.files[0];

          const rows = await readXlsxFile(file);

          await bulkInsert(rows);

          addToast({
            type: 'success',
            description: 'Importação de produtos realizada com sucesso!',
          });
        }

        inputFile = document.getElementById(
          'import-places',
        ) as HTMLInputElement;
        if (inputFile && inputFile.files && inputFile.files.length > 0) {
          const file = inputFile.files[0];

          const rows = await readXlsxFile(file);

          await bulkInsertPlaces(rows);

          addToast({
            type: 'success',
            description: 'Importação de localidades realizada com sucesso!',
          });
        }

        inputFile = document.getElementById(
          'import-clients',
        ) as HTMLInputElement;
        if (inputFile && inputFile.files && inputFile.files.length > 0) {
          const file = inputFile.files[0];

          const rows = await readXlsxFile(file);

          await chunckBulkInsertClients(rows, 0);

          addToast({
            type: 'success',
            description: 'Importação de clientes realizada com sucesso!',
          });
        }
      } catch (err: any) {
        setLoading(false);
        // console.log(err.message);
        // if (err instanceof Yup.ValidationError) {
        //   const parseErrors = getValidationErrors(err);
        //   setErrors(parseErrors);
        //   return;
        // }

        addToast({
          type: 'danger',
          description:
            'Infelizmente não foi possível completar a operação. Por favor, Tente novamente.',
        });
      }
    },
    [addToast, bulkInsert, bulkInsertPlaces],
  );

  const handleDownload = useCallback((templateNumber: number) => {
    const template: any = {
      0: 'https://firebasestorage.googleapis.com/v0/b/p100-001.appspot.com/o/templates%2Ftemplate.xlsx?alt=media&token=b16eaaf8-95f7-45c8-9cc4-ca5a6ffc2b8f',
      1: 'https://firebasestorage.googleapis.com/v0/b/p100-001.appspot.com/o/templates%2Ftemplate-localidades.xlsx?alt=media&token=a566316f-481d-4e3c-a7c8-98b908ab703b',
      2: 'https://firebasestorage.googleapis.com/v0/b/p100-001.appspot.com/o/templates%2Ftemplate-clients.xlsx?alt=media&token=730efceb-1c09-466b-8a8e-232b0860ffdd',
    };

    const url = template[templateNumber];

    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    // the filename you want
    a.download = 'template.xlsx';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  }, []);

  return (
    <div className="nk-block">
      <div className="nk-block-head nk-block-between">
        <div className="nk-block-head-content">
          <h5 className="nk-block-title">Importações</h5>
          <div className="nk-block-des">
            <p>
              Importe rapidamente todos os seus produtos e já inicie suas
              vendas.
            </p>
          </div>
        </div>
        <div className="nk-block-head-content align-self-start d-lg-none">
          <Link
            to="#menu"
            className="toggle btn btn-icon btn-trigger mt-n1"
            data-target="userAside"
            onClick={openMenu}
          >
            <em className="icon ni ni-menu-alt-r" />
          </Link>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row gy-4">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="import" className="form-label">
                Importar Produtos
              </label>
              <div className="form-control-wrap">
                <input id="import" type="file" style={{ width: '100%' }} />

                <br />

                <span className="fs-9px">
                  * Escolha o arquivo xlsx com os produtos para importação.
                </span>
              </div>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="background" className="form-label">
                Template
              </label>
              <div className="form-control-wrap">
                <button
                  type="button"
                  className="btn btn-md btn-primary"
                  onClick={() => handleDownload(0)}
                >
                  <em className="icon ni ni-download" />
                  <span>Baixar template</span>
                </button>

                <br />

                <span className="fs-9px">
                  * Clique para baixar o template de importação de produtos.
                </span>
              </div>
            </div>
          </div>

          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="import-places" className="form-label">
                Importar Localidades
              </label>
              <div className="form-control-wrap">
                <input
                  id="import-places"
                  type="file"
                  style={{ width: '100%' }}
                />

                <br />

                <span className="fs-9px">
                  * Escolha o arquivo xlsx com as localidades para importação.
                </span>
              </div>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="background" className="form-label">
                Template
              </label>
              <div className="form-control-wrap">
                <button
                  type="button"
                  className="btn btn-md btn-primary"
                  onClick={() => handleDownload(1)}
                >
                  <em className="icon ni ni-download" />
                  <span>Baixar template</span>
                </button>

                <br />

                <span className="fs-9px">
                  * Clique para baixar o template de importação de localidades.
                </span>
              </div>
            </div>
          </div>

          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="import-places" className="form-label">
                Importar Clientes
              </label>
              <div className="form-control-wrap">
                <input
                  id="import-clients"
                  type="file"
                  style={{ width: '100%' }}
                />

                <br />

                <span className="fs-9px">
                  * Escolha o arquivo xlsx com os clientes para importação.
                </span>
              </div>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="background" className="form-label">
                Template
              </label>
              <div className="form-control-wrap">
                <button
                  type="button"
                  className="btn btn-md btn-primary"
                  onClick={() => handleDownload(2)}
                >
                  <em className="icon ni ni-download" />
                  <span>Baixar template</span>
                </button>

                <br />

                <span className="fs-9px">
                  * Clique para baixar o template de importação de clientes.
                </span>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="form-group">
              <button
                type="submit"
                className="btn btn-lg btn-primary"
                disabled={loading}
              >
                {loading && (
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  />
                )}
                {!loading && 'Importar'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SettingsImport;
